import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { timeEntryService, customerService } from '../../services/api';
import {
  FormContainer,
  FormSection, 
  FormGroup, 
  Label, 
  Input, 
  Textarea, 
  Select,
  FormButton,
  FormActions,
  ErrorMessage,
  FormAlert,
  HelpText
} from '../../components/common/FormElements';
import Card from '../../components/common/Card';
import MonthlyVacationDeclaration from '../../components/employee/MonthlyVacationDeclaration';
import { formatHoursMinutes } from '../../utils/formatHours';

// Styled components for this page
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.dark};
  margin: 0;
`;

const BackButton = styled(FormButton)`
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

// Validation schema (project is optional)
const buildTimeEntrySchema = (t) => Yup.object().shape({
  customerId: Yup.string().required(t('employee.entry.validation.customerRequired')),
  projectId: Yup.string(),
  date: Yup.date().required(t('employee.entry.validation.dateRequired')),
  hours: Yup.number()
    .required(t('employee.entry.validation.hoursRequired'))
    .min(0.1, t('employee.entry.validation.hoursMin'))
    .max(24, t('employee.entry.validation.hoursMax')),
  description: Yup.string()
    .required(t('employee.entry.validation.descriptionRequired'))
    .min(3, t('employee.entry.validation.descriptionMin')),
});

const TimeEntryForm = () => {
  const { t } = useTranslation();
  const timeEntrySchema = useMemo(() => buildTimeEntrySchema(t), [t]);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Check if a date was passed via URL query parameter
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryDate = queryParams.get('date');
  
  // Initialize with passed date or today's date in YYYY-MM-DD format
  const today = new Date();
  const formattedDate = queryDate || today.toISOString().split('T')[0];

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const customersData = await customerService.getEmployeeCustomers();
        setCustomers(Array.isArray(customersData) ? customersData : []);
      } catch (err) {
        console.error(err);
        setError(t('employee.entry.loadCustomersError'));
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [t]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setSuccess(null);
      setError(null);

      // Coerce ids to numbers; project is optional
      const payload = {
        customerId: Number(values.customerId),
        date: values.date,
        hours: parseFloat(values.hours),
        description: values.description,
      };
      if (values.projectId) {
        payload.projectId = Number(values.projectId);
      }

      await timeEntryService.createTimeEntry(payload);

      if (queryDate) {
        // If the user came from the calendar view, redirect back there
        navigate('/employee/calendar');
      } else {
        // Otherwise show success message and reset form
        setSuccess(t('employee.entry.createSuccess'));
        resetForm({
          values: {
            customerId: '',
            projectId: '',
            date: formattedDate,
            hours: '',
            description: ''
          }
        });
      }
    } catch (err) {
      console.error(err);
      setError(err.message || t('employee.entry.createError'));
    } finally {
      setSubmitting(false);
    }
  };

  const fetchProjects = async (customerId) => {
    if (customerId) {
      try {
        const projectsData = await customerService.getCustomerProjects(customerId);
        setProjects(Array.isArray(projectsData) ? projectsData : []);
      } catch (err) {
        console.error('Failed to load projects:', err);
        setProjects([]);
      }
    } else {
      setProjects([]);
    }
  };

  if (loading) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <>
      <PageHeader>
        <Title>{t('employee.entry.newTitle')}</Title>
        <BackButton
          onClick={() => navigate('/employee/time-entries')}
          variant="outlined"
        >
          {t('employee.entry.backToList')}
        </BackButton>
      </PageHeader>

      <Card>
        {error && <FormAlert variant="error">{error}</FormAlert>}
        {success && <FormAlert variant="success">{success}</FormAlert>}
        
        <Formik
          initialValues={{
            customerId: '',
            projectId: '',
            date: formattedDate,
            hours: '',
            description: ''
          }}
          validationSchema={timeEntrySchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <FormContainer>
                <FormSection>
                  <FormGroup>
                    <Label htmlFor="customerId">{t('common.customer')}</Label>
                    <Select
                      id="customerId"
                      name="customerId"
                      value={values.customerId}
                      onChange={(e) => {
                        handleChange(e);
                        // Reset projectId when customer changes
                        setFieldValue('projectId', '');
                        fetchProjects(e.target.value);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    >
                      <option value="">{t('employee.entry.selectCustomer')}</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}
                        </option>
                      ))}
                    </Select>
                    <ErrorMessage name="customerId" component="div" />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="projectId">{t('common.project')}</Label>
                    <Select
                      id="projectId"
                      name="projectId"
                      value={values.projectId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting || !values.customerId}
                    >
                      <option value="">{t('employee.entry.selectProjectOptional')}</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </Select>
                    <ErrorMessage name="projectId" component="div" />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="date">{t('common.date')}</Label>
                    <Input
                      type="date"
                      id="date"
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                    <ErrorMessage name="date" component="div" />
                  </FormGroup>
                  <MonthlyVacationDeclaration year={Number(values.date.slice(0, 4))} month={Number(values.date.slice(5, 7))} />
                  
                  <FormGroup>
                    <Label htmlFor="hours">{t('common.hours')}</Label>
                    <Input
                      type="number"
                      id="hours"
                      name="hours"
                      step="0.1"
                      min="0.1"
                      max="24"
                      value={values.hours}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                    {/* The field itself stays decimal — that is what the API takes —
                        but the employee gets the H:MM reading of what they typed. */}
                    {values.hours !== '' && <HelpText>= {formatHoursMinutes(values.hours)}</HelpText>}
                    <ErrorMessage name="hours" component="div" />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="description">{t('common.description')}</Label>
                    <Textarea
                      id="description"
                      name="description"
                      rows="4"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      placeholder={t('employee.entry.descriptionPlaceholder')}
                    />
                    <ErrorMessage name="description" component="div" />
                  </FormGroup>
                </FormSection>
                
                <FormActions>
                  <BackButton
                    type="button"
                    onClick={() => navigate('/employee/time-entries')}
                    variant="outlined"
                    disabled={isSubmitting}
                  >
                    {t('common.cancel')}
                  </BackButton>
                  <FormButton
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t('common.saving') : t('employee.entry.createButton')}
                  </FormButton>
                </FormActions>
              </FormContainer>
            </Form>
          )}
        </Formik>
      </Card>
    </>
  );
};

export default TimeEntryForm;
